import { debounce } from './utils/debounce';
import ImageCropperModal from './utils/imagecropper';

class EventSpeakers {
    constructor() {

        this.form = document.getElementById("event-speakers");

        this.translations = {
            confirmRemove: this.form?.dataset.msgConfirmRemove || 'Are you sure you want to remove this speaker?',
            speakerAdded: this.form?.dataset.msgSpeakerAdded || 'Speaker added successfully!',
            speakerExists: this.form?.dataset.msgSpeakerExists || 'Speaker already added!',
            enterName: this.form?.dataset.msgEnterName || 'Please enter the speaker full name',
            addError: this.form?.dataset.msgAddError || 'Failed to add speaker!',
            confirmAdd: this.form?.dataset.msgConfirmAdd || 'There is already a participant with a similar name. Are you sure you want to add this participant instead of selecting an existing one?'
        };

        this.language = this.form?.dataset.language;
        this.nameField = this.form?.querySelector("#event-speaker-name");
        this.list = this.form?.querySelector("#event-speakers-list");
        this.addButton = this.form?.querySelector("#event-speaker-add");
        this.removeButtons = this.form?.querySelectorAll(".event-speakers-remove");
        this.autocomplete = this.form?.querySelector("#event-speaker-autocomplete");
        this.fileInput = this.form?.querySelectorAll('file-input');

        this.autoCompleteData = [];
        this.cropperModal = new ImageCropperModal();
        
        this.bindEvents();
    }

    bindEvents() {
        
        if (this.nameField) this.nameField.addEventListener("keyup", debounce(this.searchSpeakers.bind(this), 1000));
        if (this.nameField) this.nameField.addEventListener("keyup", this.handleKeyDown.bind(this));
        if (this.addButton) this.addButton.addEventListener("click", this.addSpeaker.bind(this));
        if (this.list) this.list.addEventListener("click", this.handleRemoveSpeaker.bind(this));
        if (this.list) this.list.addEventListener("click", this.handleUploadImage.bind(this));
    }

    handleKeyDown(e) {
        if (e.target.value.length == 0) {
            this.autocomplete.innerHTML = '';
        }
    }

    handleUploadImage(e) {
        if (e.target.classList.contains('upload-btn')) {
            const uploadButton = e.target.nextElementSibling;
            uploadButton.value = '';
            uploadButton.click();
            uploadButton.removeEventListener('change', this.fileUploadHandler);
            uploadButton.addEventListener('change', (e) => {
                if (e.target.files && e.target.files[0]) {
                    const reader = new FileReader();
                    const previewImage = e.target.parentElement.getElementsByTagName('img')[0];
                    
                    reader.onload = (e) => {
                        this.cropperModal.open(e.target.result, previewImage);
                        previewImage.setAttribute('data-asset-id', '');
                    };
                    
                    reader.readAsDataURL(e.target.files[0]);
                }
            });
        }
    }

    async searchSpeakers() {
        const searchTerm = this.nameField.value.trim();
        
        if (searchTerm.length < 2) return;

        try {
            const response = await fetch('/actions/speakers/speakers/search', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': document.querySelector('input[name="CRAFT_CSRF_TOKEN"]').value
                },
                body: JSON.stringify({
                    query: searchTerm
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            this.displayAutoCompleteResults(data);

        } catch (error) {
            console.error('Search error:', error);
        }
    }

    async addSpeaker(e) {
        e.preventDefault();
        
        const speakerName = this.nameField.value.trim();

        if (!speakerName || speakerName.length < 3) {
            alert(this.translations.enterName);
            return;
        }

        if (this.autoCompleteData.length > 0 && this.autoCompleteData.some(item => item.name && item.name.includes(speakerName))) {
            if (confirm(this.translations.confirmAdd)) {
                this.saveSpeaker(speakerName);
                this.nameField.value = "";
                this.autocomplete.innerHTML = '';
            }
        } else {
            this.nameField.value = "";
            this.autocomplete.innerHTML = '';
            this.saveSpeaker(speakerName)
        }
    }

    async saveSpeaker(speakerName) {
        try {
            const response = await fetch('/actions/speakers/speakers/add', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': document.querySelector('input[name="CRAFT_CSRF_TOKEN"]').value
                },
                body: JSON.stringify({
                    name: speakerName,
                })
            });
    
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
    
            const data = await response.json();
            
            if (data.success) {
                this.renderSpeaker(data, this.language);
            } else {
                alert(this.translations.addError);
            }
        } catch (error) {
        }
    }

    displayAutoCompleteResults(data) {
        this.autoCompleteData = data;
        this.autocomplete.innerHTML = '';
        data.forEach(suggestion => {
            const listItem = document.createElement('li');
            listItem.textContent = suggestion.name;
            listItem.addEventListener('click', () => this.selectAutocompleteSuggestion(suggestion));
            this.autocomplete.appendChild(listItem);
        });
    }

    async renderSpeaker(speaker, language) {
        const response = await fetch('/actions/speakers/speakers/render', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': document.querySelector('input[name="CRAFT_CSRF_TOKEN"]').value
            },
            body: JSON.stringify({
                name: speaker.name,
                id: speaker.id,
                language: language
            })
        });

        const result = await response.text();
        this.list.insertAdjacentHTML('beforeend', result);

        this.removeButtons = this.form?.querySelectorAll(".event-speakers-remove");
    }

    handleRemoveSpeaker(e) {
        if (e.target.classList.contains('event-speakers-remove')) {
            e.preventDefault();
            
            const listItem = e.target.closest('li');
            if (listItem) {
                if (confirm(this.translations.confirmRemove)) {
                    listItem.remove();
                }
            }
        }
    }

    async selectAutocompleteSuggestion(suggestion) {
        const existingItem = this.list.querySelector(`li[data-id="${suggestion.id}"]`);

        if (!existingItem) {
            this.nameField.value = "";
            this.renderSpeaker(suggestion, this.language);
        } else {
            alert(this.translations.speakerExists);
        }

        this.autocomplete.innerHTML = '';
    }

    async getBlobFromUrl(blobUrl) {
        try {
            const response = await fetch(blobUrl);
            
            if (!response.ok) {
                throw new Error(`Failed to fetch Blob. Status: ${response.status}`);
            }
            
            const blob = await response.blob();
            return blob;
        } catch (error) {
            console.error('Error fetching Blob:', error);
            throw error;
        }
    }

    async generateFormData(formData) {
        const speakers = await Promise.all(
            Array.from(this.list.querySelectorAll('li')).map(async li => {
                const imageInput = li.querySelector('input[type="file"]');
                const imgTag = li.getElementsByTagName('img')[0];
                const imageFile = imgTag?.getAttribute('src');
                let fileBase64 = '';
    
                if (imageFile) {
                    try {
                        const blob = await this.getBlobFromUrl(imageFile);
                        const file = new File([blob], 'example.jpg', { type: blob.type });
                        fileBase64 = await this.toBase64(file)
                    } catch (error) {
                        console.error('Error fetching or converting image:', error);
                    }
                }
    
                return {
                    id: li.dataset.id,
                    position: li.querySelector('input[type="text"]').value,
                    profileImage: btoa(unescape(encodeURIComponent(fileBase64))),
                    assetId: imgTag.dataset.assetId
                };
            })
        );
    
        speakers.forEach((speaker, index) => {
            formData.append(`speakers[${index}][id]`, speaker.id);
            formData.append(`speakers[${index}][position]`, speaker.position);
            formData.append(`speakers[${index}][assetId]`, speaker.assetId);
            
            if (speaker.profileImage) {
                formData.append(`speakers[${index}][profileImage]`, speaker.profileImage);
            }
        });
    }

    toBase64 = file => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
    });

    destroy() {
        if (this.nameField) {
            this.nameField.removeEventListener("keyup", this.handleKeyUp);
        }
        this.cropperModal.destroy();
    }
}

const initEventSpeakers = () => {
    if (document.getElementById("event-speakers")) {
        return new EventSpeakers();
    }
    return null;
};

export default initEventSpeakers;

window.eventSpeakers = initEventSpeakers;